import { Link, Navigate } from 'react-router-dom';
import {
  BuildingOffice2Icon,
  UserGroupIcon,
  BuildingStorefrontIcon,
  UserIcon,
  DevicePhoneMobileIcon,
} from '@heroicons/react/24/outline';
import { useAuthStore } from '@/store/auth';

interface LoginOption {
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  href: string;
  color: string;
  bgColor: string;
}

const loginOptions: LoginOption[] = [
  {
    title: 'Insurer Portal',
    description: 'For insurance company employees and administrators',
    icon: BuildingOffice2Icon,
    href: '/login/insurer',
    color: 'text-blue-600',
    bgColor: 'bg-blue-50 hover:bg-blue-100',
  },
  {
    title: 'Broker Portal',
    description: 'For brokers, agents, and POSPs',
    icon: UserGroupIcon,
    href: '/login/broker',
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-50 hover:bg-emerald-100',
  },
  {
    title: 'Channel Partner',
    description: 'For vendors and channel partners',
    icon: BuildingStorefrontIcon,
    href: '/login/channel',
    color: 'text-purple-600',
    bgColor: 'bg-purple-50 hover:bg-purple-100',
  },
  {
    title: 'Customer Portal',
    description: 'For policyholders and customers',
    icon: UserIcon,
    href: '/login/customer',
    color: 'text-orange-600',
    bgColor: 'bg-orange-50 hover:bg-orange-100',
  },
];

export function LoginSelectionPage() {
  const { isAuthenticated } = useAuthStore();

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-secondary-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl w-full">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-primary-600">base.Policy</h1>
            <p className="text-gray-500 mt-2">Insurance Broker Platform</p>
          </div>

          {/* No "Get a quote" here, deliberately (owner, 24 Sep): this is the
              generic platform site. Quoting happens only from a broker's own
              pages -- `/public/quote/<slug>`, their microsite, their profile. */}
          <h2 className="text-2xl font-semibold text-gray-900 mb-2 text-center">
            Welcome
          </h2>
          <p className="text-gray-600 text-center mb-8">
            Select your portal to continue
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {loginOptions.map((option) => (
              <Link
                key={option.href}
                to={option.href}
                className={`flex items-start p-4 rounded-xl border-2 border-transparent transition-all duration-200 ${option.bgColor}`}
              >
                <div className={`p-3 rounded-lg ${option.color} bg-white shadow-sm`}>
                  <option.icon className="h-6 w-6" />
                </div>
                <div className="ml-4">
                  <h3 className="font-semibold text-gray-900">{option.title}</h3>
                  <p className="text-sm text-gray-600 mt-1">{option.description}</p>
                </div>
              </Link>
            ))}
          </div>

          <div className="mt-6">
            <Link
              to="/login/otp"
              className="flex items-center justify-center gap-3 w-full p-4 rounded-xl border-2 border-dashed border-gray-300 hover:border-indigo-400 hover:bg-indigo-50 transition-colors"
            >
              <DevicePhoneMobileIcon className="h-5 w-5 text-indigo-600" />
              <span className="font-medium text-gray-900">Login with OTP</span>
              <span className="text-sm text-gray-500">- Sign in using mobile number</span>
            </Link>
          </div>

          <div className="mt-8 pt-6 border-t border-gray-200">
            <p className="text-center text-sm text-gray-500">
              Are you a platform administrator?{' '}
              <Link
                to="/login"
                className="font-medium text-primary-600 hover:text-primary-500"
              >
                Admin Login
              </Link>
            </p>
          </div>
        </div>

        <p className="mt-4 text-center text-sm text-gray-500">
          &copy; {new Date().getFullYear()} base.Policy.online. All rights reserved.
        </p>
      </div>
    </div>
  );
}
